import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SEO_DATA = {
  '/': {
    title: 'KenmerkCheck.nl | Betalingskenmerk Decoder voor Belastingdienst',
    description: 'Decodeer en controleer Nederlandse Belastingdienst betalingskenmerken in seconden. Gratis tool voor boekhouders, accountants en ondernemers.',
    keywords: 'betalingskenmerk, belastingdienst, decoder, btw, omzetbelasting, loonheffing'
  },
  '/uitleg': {
    title: 'Uitleg Betalingskenmerk | Hoe werkt de Belastingdienst code?',
    description: 'Leer hoe een betalingskenmerk van de Belastingdienst is opgebouwd. Ontdek de betekenis van de 16 cijfers en hoe je belastingtype, jaar en tijdvak kunt aflezen.',
    keywords: 'betalingskenmerk uitleg, belastingdienst code, 16 cijfers, mod-11'
  },
  '/pricing': {
    title: 'Prijzen | KenmerkCheck Pro en Team Abonnementen',
    description: 'Bekijk de prijzen voor KenmerkCheck Pro en Team. Bulk decodering, CSV upload, PDF naar MT940 conversie en meer voor professionals.',
    keywords: 'kenmerkcheck prijzen, pro abonnement, team abonnement, bulk decoder'
  },
  '/bulk': {
    title: 'Bulk Decoder | Meerdere Betalingskenmerken Tegelijk Controleren',
    description: 'Decodeer tot 500 betalingskenmerken tegelijk met de KenmerkCheck Bulk Decoder. Ideaal voor boekhouders en accountants met veel transacties.',
    keywords: 'bulk decoder, meerdere kenmerken, batch verwerking, boekhouder tool'
  },
  '/bank': {
    title: 'Bank Upload | Betalingskenmerken uit Bankafschrift Halen',
    description: 'Upload je bankafschrift (CSV/TXT) en laat KenmerkCheck automatisch alle betalingskenmerken herkennen en decoderen. Ondersteunt ING, Rabobank, ABN AMRO.',
    keywords: 'bankafschrift uploaden, csv upload, ing, rabobank, abn amro'
  },
  '/scan': {
    title: 'Tekst Scanner | Betalingskenmerken in Tekst Vinden',
    description: 'Plak willekeurige tekst en laat KenmerkCheck automatisch alle betalingskenmerken en aanslagnummers vinden en decoderen.',
    keywords: 'tekst scanner, kenmerken vinden, automatisch herkennen'
  },
  '/mt940': {
    title: 'PDF naar MT940 Converter | Bankafschrift Converteren',
    description: 'Converteer PDF bankafschriften naar MT940 formaat voor import in je boekhoudpakket. Ondersteunt ING, Rabobank en ABN AMRO.',
    keywords: 'pdf naar mt940, bankafschrift converter, exact online, twinfield, afas'
  },
  '/generator': {
    title: 'Betalingskenmerk Generator | Kenmerk Genereren uit Aanslagnummer',
    description: 'Genereer een geldig betalingskenmerk op basis van een aanslagnummer. Handig voor het opstellen van betaalopdrachten.',
    keywords: 'betalingskenmerk genereren, aanslagnummer omzetten, betaalopdracht'
  },
  '/extension': {
    title: 'Chrome Extensie | KenmerkCheck in je Browser',
    description: 'Download de KenmerkCheck Chrome extensie voor snelle betalingskenmerk controles direct in je browser. Gratis te downloaden.',
    keywords: 'chrome extensie, browser plugin, snel decoderen'
  },
  '/privacy': {
    title: 'Privacy Policy | KenmerkCheck.nl',
    description: 'Lees hoe KenmerkCheck omgaat met je gegevens. Wij slaan geen betalingskenmerken op en respecteren je privacy.',
    keywords: 'privacy, gegevensbescherming, avg, gdpr'
  },
  '/terms': {
    title: 'Algemene Voorwaarden | KenmerkCheck.nl',
    description: 'Bekijk de algemene voorwaarden voor het gebruik van KenmerkCheck.nl.',
    keywords: 'voorwaarden, gebruiksvoorwaarden, terms of service'
  },
  '/login': {
    title: 'Inloggen | KenmerkCheck.nl',
    description: 'Log in op je KenmerkCheck account om toegang te krijgen tot Pro functies.',
    keywords: 'inloggen, login, account',
    noindex: true
  },
  '/register': {
    title: 'Registreren | KenmerkCheck.nl',
    description: 'Maak een gratis KenmerkCheck account aan en krijg toegang tot meer functies.',
    keywords: 'registreren, account aanmaken, gratis',
    noindex: true
  }
};

// Tax type specific SEO data
const TAX_TYPE_SEO = {
  'omzetbelasting': {
    title: 'Omzetbelasting (BTW) Betalingskenmerk Decoder | KenmerkCheck',
    description: 'Decodeer BTW betalingskenmerken van de Belastingdienst. Zie direct het BTW-tijdvak, jaar en aanslagnummer. Codes: B (aangifte), F (naheffing).',
    keywords: 'btw betalingskenmerk, omzetbelasting decoder, btw aangifte, btw naheffing',
    h1: 'Omzetbelasting (BTW) Betalingskenmerk',
    intro: 'Omzetbelasting (BTW) betalingskenmerken beginnen met specifieke codes die aangeven of het een reguliere aangifte (B) of naheffingsaanslag (F) betreft. Het tijdvak kan maandelijks, per kwartaal of jaarlijks zijn.'
  },
  'loonheffing': {
    title: 'Loonheffing Betalingskenmerk Decoder | KenmerkCheck',
    description: 'Decodeer Loonheffing betalingskenmerken. Ontdek het aangiftetijdvak, jaar en type (aangifte L of naheffing A).',
    keywords: 'loonheffing betalingskenmerk, loonaangifte decoder, lh naheffing',
    h1: 'Loonheffing Betalingskenmerk',
    intro: 'Loonheffing betalingskenmerken worden gebruikt voor loonaangiften (code L) en naheffingsaanslagen (code A). Het tijdvak is meestal per 4 weken of per maand.'
  },
  'inkomstenbelasting': {
    title: 'Inkomstenbelasting Betalingskenmerk Decoder | KenmerkCheck',
    description: 'Decodeer Inkomstenbelasting betalingskenmerken (IB). Zie of het een voorlopige of definitieve aanslag betreft en voor welk belastingjaar.',
    keywords: 'inkomstenbelasting betalingskenmerk, ib decoder, voorlopige aanslag, definitieve aanslag',
    h1: 'Inkomstenbelasting (IB) Betalingskenmerk',
    intro: 'Inkomstenbelasting betalingskenmerken (code H) geven aan of het een voorlopige aanslag, definitieve aanslag of navorderingsaanslag betreft. Het soortcijfer bepaalt het type aanslag.'
  },
  'vennootschapsbelasting': {
    title: 'Vennootschapsbelasting Betalingskenmerk Decoder | KenmerkCheck',
    description: 'Decodeer Vennootschapsbelasting (VPB) betalingskenmerken. Ontdek het boekjaar en type aanslag voor uw BV of NV.',
    keywords: 'vennootschapsbelasting betalingskenmerk, vpb decoder, bv belasting',
    h1: 'Vennootschapsbelasting (VPB) Betalingskenmerk',
    intro: 'Vennootschapsbelasting betalingskenmerken (code V) zijn specifiek voor rechtspersonen zoals BV\'s en NV\'s. De structuur verschilt van particuliere belastingen.'
  },
  'motorrijtuigenbelasting': {
    title: 'Motorrijtuigenbelasting Betalingskenmerk Decoder | KenmerkCheck',
    description: 'Decodeer MRB betalingskenmerken. Controleer wegenbelasting kenmerken voor personenauto\'s, motoren en bedrijfsvoertuigen.',
    keywords: 'motorrijtuigenbelasting betalingskenmerk, mrb decoder, wegenbelasting',
    h1: 'Motorrijtuigenbelasting (MRB) Betalingskenmerk',
    intro: 'Motorrijtuigenbelasting betalingskenmerken (code M voor aangifte, Y voor naheffing) worden gebruikt voor de wegenbelasting van voertuigen.'
  },
  'toeslagen': {
    title: 'Toeslagen Betalingskenmerk Decoder | KenmerkCheck',
    description: 'Decodeer Toeslagen betalingskenmerken. Herken zorgtoeslag, huurtoeslag, kinderopvangtoeslag en kindgebonden budget codes.',
    keywords: 'toeslagen betalingskenmerk, zorgtoeslag decoder, huurtoeslag, kinderopvangtoeslag',
    h1: 'Toeslagen Betalingskenmerk',
    intro: 'Toeslagen betalingskenmerken (code T) worden gebruikt voor terugvorderingen van zorgtoeslag, huurtoeslag, kinderopvangtoeslag en kindgebonden budget.'
  },
  'zorgverzekeringswet': {
    title: 'Zorgverzekeringswet (ZVW) Betalingskenmerk Decoder | KenmerkCheck',
    description: 'Decodeer ZVW betalingskenmerken voor de inkomensafhankelijke bijdrage Zorgverzekeringswet.',
    keywords: 'zvw betalingskenmerk, zorgverzekeringswet decoder, inkomensafhankelijke bijdrage',
    h1: 'Zorgverzekeringswet (ZVW) Betalingskenmerk',
    intro: 'ZVW betalingskenmerken (code W) worden gebruikt voor de inkomensafhankelijke bijdrage Zorgverzekeringswet die werkgevers en zelfstandigen betalen.'
  }
};

export function useSEO(customTitle, customDescription) {
  const location = useLocation();
  
  useEffect(() => {
    const path = location.pathname;
    let seoData = SEO_DATA[path] || SEO_DATA['/'];
    
    // Check for tax type pages
    if (path.startsWith('/belastingtype/')) {
      const taxType = path.split('/belastingtype/')[1]?.replace(/\/$/, '');
      if (taxType && TAX_TYPE_SEO[taxType]) {
        seoData = TAX_TYPE_SEO[taxType];
      }
    }
    
    // Use custom values if provided
    const title = customTitle || seoData.title;
    const description = customDescription || seoData.description;
    
    // Update document title
    document.title = title;
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }
    
    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords && seoData.keywords) {
      metaKeywords.setAttribute('content', seoData.keywords);
    }
    
    // Update Open Graph tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', title);
    }
    
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', description);
    }
    
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', `https://kenmerkcheck.nl${path}`);
    }
    
    // Update Twitter tags
    let twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', title);
    }
    
    let twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', description);
    }
    
    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `https://kenmerkcheck.nl${path}`);
    }
    
    // Handle noindex pages
    let robots = document.querySelector('meta[name="robots"]');
    if (robots) {
      if (seoData.noindex) {
        robots.setAttribute('content', 'noindex, nofollow');
      } else {
        robots.setAttribute('content', 'index, follow');
      }
    }
    
  }, [location.pathname, customTitle, customDescription]);
}

export { TAX_TYPE_SEO };
export default useSEO;
